import { useEffect, useState } from "react";
// tp-logo.png is the "Transferpitch — Ausgeschrieben" wordmark on a
// transparent ground: blue TP icon block + "TRANSFER" in TP-blue with
// "PITCH" reversed-out on a blue pill. We render it as-is (no filter)
// on a white placeholder background so the brand colours read correctly.
// (md5-identical to Logo Ordner/Transferpitch - Ausgeschrieben/Transparent.png)
import tpLogo from "../../assets/images/tp-logo.png";

/**
 * News image with a TransferPitch-branded placeholder when the source has no
 * image or the image fails to load. Used everywhere a news image is rendered
 * so the layout never collapses.
 *
 * Props:
 *   - imageUrl:   string | null
 *   - alt:        string
 *   - sourceName: string  (optional, shown in placeholder caption)
 *   - className:  string  (passed to outer wrapper — for aspect ratio etc.)
 *   - rounded:    string  Tailwind rounded utility class (default 'rounded-lg')
 *   - fit:        'contain' | 'cover'  How the image sits in its box.
 *                 Default 'contain': the WHOLE image is always shown (never
 *                 cropped) on the neutral background, letterboxed if its ratio
 *                 differs from the box. A portrait photo keeps its head; a wide
 *                 photo shows fully. Pass 'cover' only where a flush edge-to-edge
 *                 fill is intentional and cropping is acceptable.
 */
const NewsImageWithFallback = ({
  imageUrl,
  alt = "",
  sourceName,
  className = "aspect-video w-full",
  rounded = "rounded-lg",
  fit = "contain",
}) => {
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(imageUrl) && !errored;

  // Reset the error flag when the image URL changes (e.g. cycling through
  // news cards on the Home carousel). Otherwise an erroring image on one
  // article would suppress a valid image on the next.
  useEffect(() => { setErrored(false); }, [imageUrl]);

  return (
    <div
      className={`relative overflow-hidden ${rounded} ${className}`}
      style={{ backgroundColor: "#F1F3F5" }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="lazy"
          onError={() => setErrored(true)}
          // Default object-contain + centered: the WHOLE image is shown inside
          // the box with no cropping and no distortion. If the image ratio
          // differs from the box it is letterboxed onto the neutral ground, so
          // a portrait photo keeps its head and a wide photo is fully visible.
          // 'cover' (fill, centre-crop) is opt-in for the rare case where an
          // edge-to-edge fill is wanted and cropping is acceptable.
          className={`absolute inset-0 w-full h-full object-center ${
            fit === "cover" ? "object-cover" : "object-contain"
          }`}
        />
      ) : (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
          style={{ background: "#FFFFFF" }}
          aria-label={sourceName ? `Image courtesy of ${sourceName}` : "TransferPitch"}
        >
          <img
            src={tpLogo}
            alt="TransferPitch"
            className="w-[50%] max-w-[280px] h-auto"
          />
          {sourceName && (
            <div
              className="mt-3 text-xs font-medium tracking-wide"
              style={{ color: "#6B7280", opacity: 0.7 }}
            >
              Image courtesy of {sourceName}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsImageWithFallback;
